/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import PulseLoader from 'react-spinners/PulseLoader';
import { useQuery, gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import useStyles from 'isomorphic-style-loader/useStyles';

// Material UI
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';

// app modules: components
import Heading from 'components/Heading';
import SubscriptionPlanCard from 'components/SubscriptionPlanCard';

// app: assets
import CssSubscriptionPlanChooser from './SubscriptionPlanChooser.scss';

const GET_PRICES = gql`
  query GetPrices(
    $provider: String!
    $monthlyPlanId: String!
    $annualPlanId: String!
    $overridePlanId: String
    $country: String
    $coupon: String
  ) {
    monthlyPrice: Price(
      provider: $provider
      productId: $monthlyPlanId
      country: $country
      coupon: $coupon
    ) {
      ...PriceFields
    }
    annualPrice: Price(
      provider: $provider
      productId: $annualPlanId
      country: $country
      coupon: $coupon
    ) {
      ...PriceFields
    }
    overridePrice: Price(
      provider: $provider
      productId: $overridePlanId
      country: $country
      coupon: $coupon
    ) {
      ...PriceFields
      display_name
    }
  }
  fragment PriceFields on Price {
    coupon
    currency
    interval
    list_price
    monthly_equivalent_price
    offer_price
    period
    trial_days
    product_id
  }

`;

function WebSubscriptionPlanChooser(props) {
  const {
    annualPlanId,
    buttonText,
    heading,
    hideButton,
    hideCoupon,
    hidePromotionalRateWarning,
    inProgress,
    monthlyPlanId,
    onChange,
    onSubmit,
    overridePlanId,
    selectedPlanId,
    showPromoTermInfo,
    theme,
    withZeroPriceTrials,
  } = props;

  useStyles(CssSubscriptionPlanChooser);
  const requestedCoupon = useSelector(
    state => state.paywall.requestedCoupon || props.requestedCoupon,
  );
  const defaultCoupon = useSelector(state => state.paywall.defaultCoupon);
  const defaultCouponMessage = useSelector(state => state.paywall.defaultCouponMessage);
  const country = useSelector(state => state.paywall.country || state.user.geolocation.countryCode);
  const useOverridePrice =
    // eslint-disable-next-line eqeqeq
    overridePlanId && overridePlanId != annualPlanId && overridePlanId != monthlyPlanId;

  const { loading, error, data } = useQuery(GET_PRICES, {
    variables: {
      country,
      coupon: requestedCoupon,
      annualPlanId: String(annualPlanId),
      monthlyPlanId: String(monthlyPlanId),
      overridePlanId: useOverridePrice ? String(overridePlanId) : null,
      provider: 'paddle',
    },
  });

  const handleChange = newValue => {
    if (typeof onChange === 'function' && newValue) {
      const selectedProductKey = Object.keys(data).find(
        key => data[key] && data[key].product_id === newValue,
      );
      onChange(newValue, data[selectedProductKey]);
    }
  };

  useEffect(
    () => {
      if (data && selectedPlanId) {
        handleChange(selectedPlanId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, handleChange],
  );

  if (error) {
    console.error('Cannot fetch paddle prices', error);
    return 'Cannot display the checkout right now.';
  }

  let couponInfo = null;
  if (
    requestedCoupon &&
    data &&
    ((data.monthlyPrice && data.monthlyPrice.coupon === requestedCoupon) ||
      (data.annualPrice && data.annualPrice.coupon === requestedCoupon) ||
      (data.overridePrice && data.overridePrice.coupon === requestedCoupon))
  ) {
    if (requestedCoupon === defaultCoupon) {
      if (defaultCouponMessage) {
        couponInfo = (
          <p align="center">
            <strong>{defaultCouponMessage}</strong>
          </p>
        );
      }
    } else {
      couponInfo = (
        <p align="center">
          Coupon applied: <strong>{requestedCoupon}</strong>
        </p>
      );
    }
  }
  let overridePriceTitle;
  if (!loading && data.overridePrice) {
    const { interval, period, display_name } = data.overridePrice;
    overridePriceTitle = display_name || `${interval} ${period}`;
  }

  // Let the user know their promo is only going to last a year.
  // Only show this message if they are actually buying something with a discount.
  let promoTermInfo = null;
  if (showPromoTermInfo && data) {
    const selectedPlanPrice = [data.monthlyPrice, data.annualPrice, data.overridePrice].find(
      price => price?.product_id === selectedPlanId,
    );

    const selectedPlanDiscount =
      selectedPlanPrice &&
      selectedPlanPrice.list_price !== selectedPlanPrice.offer_price &&
      selectedPlanPrice.coupon;
    if (selectedPlanDiscount && !hidePromotionalRateWarning) {
      promoTermInfo = (
        <p className="centered">
          Promotional rate applies to the first{' '}
          {selectedPlanPrice.period === 'year' ? 'year' : '12 months'} of subscription
        </p>
      );
    }
  }
  return (
    <React.Fragment>
      <div className={`subscription-plan-chooser subscription-plan-chooser--theme-${theme}`}>
        <div className="subscription-plan-chooser__wrapper">
          {hideCoupon ? null : couponInfo}
          {heading ? (
            <Heading className="heading heading--size-32 centered">{heading}</Heading>
          ) : null}
          <Grid
            container
            spacing={2}
            className="margin-bottom-5 margin-top-5 subscription-plan-chooser__grid"
          >
            {useOverridePrice ? (
              <Grid item xs={12}>
                <SubscriptionPlanCard
                  loading={loading}
                  onClick={() => handleChange(overridePlanId)}
                  priceInfo={data ? data.overridePrice : null}
                  productId={overridePlanId}
                  selected={selectedPlanId === overridePlanId}
                  theme={theme}
                  title={overridePriceTitle}
                  withZeroPriceTrials={withZeroPriceTrials}
                />
              </Grid>
            ) : (
              <React.Fragment>
                <Grid item xs={6}>
                  <SubscriptionPlanCard
                    loading={loading}
                    onClick={() => handleChange(monthlyPlanId)}
                    priceInfo={data ? data.monthlyPrice : null}
                    productId={monthlyPlanId}
                    selected={selectedPlanId === monthlyPlanId}
                    unselected={selectedPlanId && selectedPlanId !== monthlyPlanId}
                    theme={theme}
                    title="Monthly plan"
                    withZeroPriceTrials={withZeroPriceTrials}
                  />
                </Grid>
                <Grid item xs={6}>
                  <SubscriptionPlanCard
                    chip="Best value"
                    loading={loading}
                    onClick={() => handleChange(annualPlanId)}
                    priceInfo={data ? data.annualPrice : null}
                    productId={annualPlanId}
                    selected={selectedPlanId === annualPlanId}
                    unselected={selectedPlanId && selectedPlanId !== annualPlanId}
                    theme={theme}
                    title="Annual plan"
                    withZeroPriceTrials={withZeroPriceTrials}
                  />
                </Grid>
              </React.Fragment>
            )}
          </Grid>
          <div className={hideButton ? null : 'subscription-plan-chooser__button-wrapper'}>
            {selectedPlanId && !hideButton ? (
              <Button
                fullWidth
                variant="contained"
                size="large"
                color="primary"
                onClick={() => onSubmit(selectedPlanId)}
                data-cy="button-confirm-plan-choice"
              >
                {inProgress ? (
                  <PulseLoader loading color="#fff" sizeUnit="px" size={10} />
                ) : (
                  buttonText
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {promoTermInfo}
    </React.Fragment>
  );
}

WebSubscriptionPlanChooser.propTypes = {
  annualPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  buttonText: PropTypes.string,
  heading: PropTypes.string,
  hideButton: PropTypes.bool,
  hideCoupon: PropTypes.bool,
  hidePromotionalRateWarning: PropTypes.bool,
  inProgress: PropTypes.bool,
  monthlyPlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  overridePlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  requestedCoupon: PropTypes.string,
  selectedPlanId: PropTypes.string,
  showPromoTermInfo: PropTypes.bool,
  theme: PropTypes.oneOf(['dark', 'light', 'system']),
  withZeroPriceTrials: PropTypes.bool,
};

WebSubscriptionPlanChooser.defaultProps = {
  buttonText: 'Start Premium Membership',
  heading: null,
  hideButton: false,
  hideCoupon: false,
  hidePromotionalRateWarning: false,
  inProgress: false,
  onChange: null,
  overridePlanId: null,
  requestedCoupon: null,
  selectedPlanId: null,
  showPromoTermInfo: false,
  theme: 'light',
  withZeroPriceTrials: false,
};

export default WebSubscriptionPlanChooser;
